import { useState, useRef } from 'react'
import { Upload, Palette, X, Download } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function PaletteGenerator() {
  const [colors, setColors] = useState([])
  const [imageUrl, setImageUrl] = useState(null)
  const fileInputRef = useRef(null)

  const extractColorsFromImage = (image) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    canvas.width = image.width
    canvas.height = image.height
    ctx.drawImage(image, 0, 0)

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const data = imageData.data
    const colorMap = {}

    // Sample pixels (every 10th pixel for performance)
    for (let i = 0; i < data.length; i += 40) {
      const r = data[i]
      const g = data[i + 1]
      const b = data[i + 2]
      const a = data[i + 3]

      if (a < 255) continue // Skip transparent pixels

      const hex = `#${[r, g, b].map(x => {
        const hex = x.toString(16)
        return hex.length === 1 ? '0' + hex : hex
      }).join('')}`

      colorMap[hex] = (colorMap[hex] || 0) + 1
    }

    // Get top 8 most common colors
    const sortedColors = Object.entries(colorMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([hex]) => hex)

    setColors(sortedColors)
  }

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0]
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onload = (event) => {
        const url = event.target.result
        setImageUrl(url)
        const img = new Image()
        img.onload = () => {
          extractColorsFromImage(img)
        }
        img.src = url
      }
      reader.readAsDataURL(file)
    }
  }

  const addColor = () => {
    const newColor = '#000000'
    setColors([...colors, newColor])
  }

  const updateColor = (index, newColor) => {
    const updated = [...colors]
    updated[index] = newColor
    setColors(updated)
  }

  const removeColor = (index) => {
    setColors(colors.filter((_, i) => i !== index))
  }

  const clearAll = () => {
    setColors([])
    setImageUrl(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const copyPalette = () => {
    const paletteText = colors.join(', ')
    navigator.clipboard.writeText(paletteText)
    alert('Palette copied to clipboard!')
  }

  return (
    <div className="space-y-6">
      <Card>
        <Card.Body>
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">Upload Image (Optional):</label>
            <div className="flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                id="palette-image-upload"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                type="button"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Image
              </Button>
              {imageUrl && (
                <div className="flex items-center gap-2">
                  <img src={imageUrl} alt="Uploaded" className="w-12 h-12 object-cover rounded border" />
                  <button
                    onClick={() => {
                      setImageUrl(null)
                      if (fileInputRef.current) fileInputRef.current.value = ''
                    }}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <X className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Upload an image to automatically extract a color palette, or manually add colors below
            </p>
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-gray-700 font-semibold">Color Palette:</label>
              <div className="flex gap-2">
                <Button onClick={addColor} variant="outline" size="sm">
                  Add Color
                </Button>
                <Button onClick={copyPalette} variant="outline" size="sm" disabled={colors.length === 0}>
                  Copy Palette
                </Button>
                <Button onClick={clearAll} variant="outline" size="sm">
                  Clear
                </Button>
              </div>
            </div>

            {colors.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
                {colors.map((color, index) => (
                  <div key={index} className="space-y-2">
                    <div className="relative">
                      <div
                        className="w-full h-24 rounded-lg border-2 border-gray-200 cursor-pointer"
                        style={{ backgroundColor: color }}
                      />
                      <button
                        onClick={() => removeColor(index)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <input
                      type="color"
                      value={color}
                      onChange={(e) => updateColor(index, e.target.value)}
                      className="w-full h-8 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={color}
                      onChange={(e) => updateColor(index, e.target.value)}
                      className="w-full text-xs px-2 py-1 border border-gray-300 rounded"
                      placeholder="#000000"
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <Palette className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600 text-sm">No colors in palette. Upload an image or add colors manually.</p>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}

